import { useContext } from 'react';
import { Box, Heading } from '@zillow/constellation-10';
import { FolderSelector } from '@zg-rentals/satellite-components';

import type { Folder } from '../../types/GraphqlTypes';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import StringConstants from '../../constants/StringConstants';
import { AppContext } from '../../contexts/AppContextProvider';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { isZillow } from '@zg-rentals/multibrand-support';
import RentingBuyingPills from '../RentingBuyingPills';
import { headerWrapperStyles, zillowFolderSelectorStyles, zillowHeaderWrapperStyles } from './styles';
import useGetFeatureFlagsAndSettings from '../../hooks/useGetFeatureFlagsAndSettings';

type Props = {
  onFolderChange(selectedOption: Folder): void;
};

export default function Header({ onFolderChange }: Props): JSX.Element {
  const { brand } = useContext(AppContext);
  const { mobileView, folder } = useContext(InboxPageContext);
  const isZillowBrand = isZillow(brand);
  const { features } = useGetFeatureFlagsAndSettings();
  const { enableRentingBuyingPills } = features;
  const shouldShowPills = enableRentingBuyingPills && isZillowBrand;

  const handleFolderChange = (selectedOption: Folder) => {
    onFolderChange(selectedOption);
  };

  const isMessageListView = mobileView === InboxPageMobileView.MessageList;

  return (
    <Box
      asChild
      css={{
        ...headerWrapperStyles,
        display: {
          base: isMessageListView ? 'none' : 'block',
          lg: 'block',
        },
      }}
    >
      <header>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            ...(shouldShowPills ? zillowHeaderWrapperStyles : {}),
          }}
        >
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Heading
              level={1}
              fontFamily="heading-alt"
              css={{
                position: 'relative',
                top: '-1px',
                textStyle: {
                  lgDown: 'h4',
                  lg: {
                    base: 'h3',
                    _hotpadsTheme: 'h4',
                  },
                },
              }}
            >
              {isZillowBrand ? StringConstants.ZILLOW_APP_TITLE : StringConstants.HOTPADS_APP_TITLE}
            </Heading>

            {shouldShowPills && <RentingBuyingPills />}
          </Box>

          <Box
            css={{
              marginLeft: 'loose',
              ...(shouldShowPills ? zillowFolderSelectorStyles : {}),
            }}
          >
            <FolderSelector
              selectedFolder={folder}
              actions={{ onFolderChange: handleFolderChange }}
              persona="renter"
              brand={brand}
            />
          </Box>
        </Box>
      </header>
    </Box>
  );
}
